import Head from 'next/head'
import Link from 'next/link'
import { FormEventHandler, useEffect, useState } from 'react'

import AuthenticationLayout from '@/components/layouts/Authentication'
import { useDependencies } from '@/components/providers/DependencyProvider'
import { Status, useStatus } from '@/hooks/useStatus'

const formatForTimer = (number: number): string => String(number).padStart(2, '0').slice(0, 2)

function Timer({ expires }: { expires: Date }): JSX.Element {
	const [now, setNow] = useState(new Date())

	useEffect(() => {
		const interval = setInterval(() => {
			setNow(new Date())
		}, 500)

		return () => clearInterval(interval)
	})

	let difference = (expires.getTime() - now.getTime()) / 1000
	const days = Math.floor(difference / 86400)
	difference -= days * 86400

	const hours = Math.floor(difference / 3600) % 24
	difference -= hours * 24

	const minutes = Math.floor(difference / 60) % 60
	difference -= minutes * 60

	const seconds = Math.floor(difference % 60)

	return (
		<span className="font-extrabold text-green-400">{`${formatForTimer(minutes)}:${formatForTimer(seconds)}`}</span>
	)
}

export default function ForgotPassword(): JSX.Element {
	const { apiFetcher } = useDependencies()
	const [email, setEmail] = useState('')
	const [expires, setExpires] = useState(new Date())
	const [status, setStatus] = useStatus(Status.Idle)

	const handleForgotPassword: FormEventHandler = event => {
		event.preventDefault()

		setStatus(Status.Loading)

		apiFetcher('/forgot-password', {
			body: JSON.stringify({ email }),
			method: 'POST',
		}).then(() => {
			const now = new Date()

			setExpires(new Date(now.getTime() + (1 * 60 * 60 * 1000) - 1000))
			setStatus(Status.Success)
		}).catch(() => {
			setStatus(Status.Error)
		})
	}

	return (
		<form className="mx-auto w-full max-w-xs text-gray-800 space-y-6 bg-white shadow-card rounded-lg pt-3 px-3 pb-6 md:max-w-md md:p-8 md:space-y-9 xl:max-w-xl" onSubmit={handleForgotPassword}>
			<Head>
				<title key="page-title">Forgot Password | Pace International</title>
			</Head>

			{ status === Status.Success ? (
				<>
					<h1 className="text-xl font-bold text-center md:text-3xl">Email Sent!</h1>
					<p>An email has been sent to the email address given. Check the inbox and click the reset link provided.</p>

					<p className="text-center text-xl">The reset link will expire in <Timer expires={expires} /></p>
				</>
			) : (
				<>
					<h1 className="text-xl font-bold text-center md:text-3xl">Reset your password</h1>
					<p className="text-sm text-center px-3 md:text-base md:px-0">Enter the email address associated with your account and we’ll send you a link to reset your password.</p>

					<div>
						<div className="">
							<label className="text-sm md:text-base" htmlFor="email">Email</label>
						</div>
						<input className="mt-1.5 px-2 py-1.5 text-sm shadow-input appearance-none w-full h-8 rounded md:h-11 md:rounded-lg" id="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} />
					</div>

					{ status === Status.Error ? <p className="text-center text-red-700 font-bold">An unexpected error occurred, try again later.</p> : null}

					<button className="py-2 w-full bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase text-base font-extrabold leading-5 md:h-11 md:text-lg" type="submit">{status === Status.Loading ? 'Submitting' : 'Submit'}</button>
				</>
			)}

			<div className="flex justify-center space-x-3">
				<Link href="/login"><a className="block underline text-gray-800 text-sm font-semibold md:text-base">Return to Sign In</a></Link>
				<span>|</span>
				<Link href="/"><a className="block underline text-gray-800 text-sm font-semibold md:text-base">Return to home</a></Link>
			</div>
		</form>
	)
}

ForgotPassword.Layout = AuthenticationLayout
